/*
 * Scroll-triggered word reveal
 * ---------------------------------------------------------------------------
 * Words rise and fade in one after another when a heading scrolls into view.
 * Scrolling only triggers the reveal - the animation itself is time based, so
 * it always plays through even if the visitor stops scrolling mid-way.
 *
 * A template for local use: on its own it runs on nothing. It animates only an
 * element explicitly marked class="scroll-text", in a section that renders
 * snippets/scroll-text-variables.liquid (which loads this engine and scopes
 * the timing and movement from Theme settings > Text animations to that
 * section). No tag, heading class or section is ever targeted automatically.
 *
 * Per element options:
 *   class="scroll-text"                 animate this element
 *   data-scroll-text="off"              switch it back off (also works on a wrapper)
 *   data-scroll-text-duration="400"     ms a single word takes to fade in
 *   data-scroll-text-stagger="40"       ms between one word and the next
 *   data-scroll-text-dim="0.12"         opacity of a word before it is revealed
 *   data-scroll-text-rise="0.25"        em a word rises while revealing (0 disables)
 *   data-scroll-text-once="true"        never replay, even after scrolling away
 */

if (typeof window.ScrollText === "undefined") {
  // Runs only on elements that are explicitly marked.
  const SELECTOR = ".scroll-text";
  const EXCLUDE = "[data-scroll-text=\"off\"], template, noscript";
  const WORD_CLASS = "scroll-text__word";
  const READY_CLASS = "scroll-text--ready";
  const REVEALED_CLASS = "scroll-text--revealed";
  const INSTANT_CLASS = "scroll-text--instant";

  // Engine defaults. Stagger and replay arrive per section as custom properties
  // from the snippet (fed by Theme settings > Text animations); the trigger line
  // is store-wide and arrives through window.ScrollTextSettings.
  const SETTINGS = window.ScrollTextSettings || {};
  const STAGGER = SETTINGS.stagger >= 0 ? SETTINGS.stagger : 40;
  const TRIGGER = SETTINGS.trigger >= 0 ? SETTINGS.trigger : 12;
  const REPLAY = SETTINGS.replay !== false;

  // Longer blocks tighten their stagger so a paragraph never crawls.
  const MAX_STAGGER_SPAN = 900;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  class ScrollTextItem {
    constructor(element) {
      this.element = element;
      this.options = this.readOptions();

      // A clone of an element that was already split keeps its word spans and
      // delays; it only needs to be observed again.
      element.classList.remove(REVEALED_CLASS);
      element.classList.add(READY_CLASS, INSTANT_CLASS);

      const existing = element.querySelectorAll(`.${WORD_CLASS}`);
      if (existing.length) {
        this.words = Array.from(existing);
        return;
      }

      this.words = this.splitWords();
      if (!this.words.length) {
        element.classList.remove(READY_CLASS, INSTANT_CLASS);
        return;
      }

      const { dim, rise, duration } = this.options;
      if (dim !== null) this.element.style.setProperty("--scroll-text-dim", dim);
      if (rise !== null) this.element.style.setProperty("--scroll-text-rise", `${rise}em`);
      if (duration !== null) this.element.style.setProperty("--scroll-text-duration", `${duration}ms`);

      const steps = Math.max(1, this.groups.length - 1);
      const stagger = Math.min(this.options.stagger, MAX_STAGGER_SPAN / steps);

      this.groups.forEach((group, index) => {
        const delay = `${Math.round(index * stagger)}ms`;
        group.forEach((word) => word.style.setProperty("--scroll-text-delay", delay));
      });

      this.element.classList.add(READY_CLASS);
    }

    readOptions() {
      const data = this.element.dataset;
      const styles = window.getComputedStyle(this.element);
      const number = (value, fallback = null) => {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? fallback : parsed;
      };
      // Section level values, scoped to the section by the snippet.
      const scoped = (name, fallback) => number(styles.getPropertyValue(name), fallback);

      return {
        duration: number(data.scrollTextDuration),
        stagger: Math.max(0, number(data.scrollTextStagger, scoped("--scroll-text-stagger", STAGGER))),
        dim: number(data.scrollTextDim),
        rise: number(data.scrollTextRise),
        once:
          data.scrollTextOnce === undefined
            ? scoped("--scroll-text-replay", REPLAY ? 1 : 0) === 0
            : data.scrollTextOnce !== "false",
      };
    }

    // Only text nodes are replaced, so inline markup (links, <span italic>, <br>) survives.
    // Tokens that are not separated by whitespace stay in one group, so punctuation
    // sitting outside a tag ("<span italic>Pro</span>.") reveals with the word it belongs to.
    splitWords() {
      const walker = document.createTreeWalker(this.element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
        acceptNode: (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            return node.tagName === "BR" ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
          }

          const parent = node.parentNode;
          if (!node.nodeValue || (parent.classList && parent.classList.contains(WORD_CLASS))) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        },
      });

      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      const ancestors = nodes.map((node) => (node.nodeType === Node.TEXT_NODE ? this.blockAncestor(node.parentNode) : null));

      const words = [];
      let block = null;
      let broken = true;

      this.groups = [];

      nodes.forEach((node, index) => {
        if (node.nodeType === Node.ELEMENT_NODE || !node.nodeValue.trim()) {
          broken = true;
          return;
        }

        const ancestor = ancestors[index];
        if (ancestor !== block) broken = true;
        block = ancestor;

        const fragment = document.createDocumentFragment();

        node.nodeValue.split(/(\s+)/).forEach((token) => {
          if (!token) return;

          if (!token.trim()) {
            broken = true;
            fragment.appendChild(document.createTextNode(token));
            return;
          }

          const word = document.createElement("span");
          word.className = WORD_CLASS;
          word.textContent = token;
          fragment.appendChild(word);
          words.push(word);

          if (broken || !this.groups.length) this.groups.push([]);
          this.groups[this.groups.length - 1].push(word);
          broken = false;
        });

        node.parentNode.replaceChild(fragment, node);
      });

      return words;
    }

    blockAncestor(element) {
      let node = element;

      while (node && node !== this.element) {
        const display = window.getComputedStyle(node).display;
        if (display !== "inline" && display !== "inline-block") return node;
        node = node.parentElement;
      }

      return this.element;
    }

    reveal() {
      this.element.classList.add(REVEALED_CLASS);
    }

    // Rearming happens off screen, so drop back to the hidden state without
    // running the transition in reverse.
    reset() {
      this.element.classList.add(INSTANT_CLASS);
      this.element.classList.remove(REVEALED_CLASS);
      void this.element.offsetWidth;
      this.element.classList.remove(INSTANT_CLASS);
    }

    destroy() {
      this.element.classList.remove(READY_CLASS, REVEALED_CLASS, INSTANT_CLASS);
      this.element.style.removeProperty("--scroll-text-dim");
      this.element.style.removeProperty("--scroll-text-rise");
      this.element.style.removeProperty("--scroll-text-duration");
    }
  }

  const items = new Map();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const item = items.get(entry.target);
        if (!item) return;

        if (entry.isIntersecting) {
          item.reveal();
          if (item.options.once) observer.unobserve(entry.target);
          return;
        }

        const rect = entry.boundingClientRect;
        if (rect.top >= window.innerHeight || rect.bottom <= 0) item.reset();
      });
    },
    { rootMargin: `0px 0px -${TRIGGER}% 0px` }
  );

  // `settled` marks a heading the visitor can already see: it is shown as is,
  // with no dim state and no transition, so text that has painted never
  // disappears and re-animates. It still replays after scrolling away.
  const mount = (element, settled) => {
    if (items.has(element) || reducedMotion.matches) return;
    if (element.closest(EXCLUDE)) return;
    if (element.parentElement && element.parentElement.closest(SELECTOR)) return;

    const item = new ScrollTextItem(element);
    if (!item.words.length) return;

    items.set(element, item);
    if (settled) element.classList.add(REVEALED_CLASS);

    window.requestAnimationFrame(() => {
      element.classList.remove(INSTANT_CLASS);
      window.requestAnimationFrame(() => {
        if (items.get(element) === item) observer.observe(element);
      });
    });
  };

  // The initial pass (and a theme editor re-render) runs after first paint, so
  // headings already in view are settled. Later-injected content is dimmed
  // before it ever paints and keeps its entrance.
  const scan = (root, settleVisible) => {
    if (reducedMotion.matches) return;
    const elements = Array.from((root || document).querySelectorAll(SELECTOR));
    const line = window.innerHeight * (1 - TRIGGER / 100);
    const visible = settleVisible
      ? elements.map((element) => {
          const rect = element.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < line && rect.width > 0;
        })
      : [];
    elements.forEach((element, index) => mount(element, settleVisible && visible[index]));
  };

  const unmount = (root) => {
    items.forEach((item, element) => {
      if (root && !root.contains(element)) return;

      observer.unobserve(element);
      item.destroy();
      items.delete(element);
    });
  };

  const mutations = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches(SELECTOR)) mount(node);
        else scan(node);
      });
      record.removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) unmount(node);
      });
    });
  });
  mutations.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener("shopify:section:load", (event) => scan(event.target, true));
  document.addEventListener("shopify:section:unload", (event) => unmount(event.target));

  if (typeof reducedMotion.addEventListener === "function") {
    reducedMotion.addEventListener("change", () => (reducedMotion.matches ? unmount() : scan(undefined, true)));
  }

  window.ScrollText = { scan, unmount };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan(undefined, true));
  } else {
    scan(undefined, true);
  }
}
